import {CreateQuoteItemDto, QuoteItemDto} from "../../api/model/rest";
import {none, option, Option} from "ts-option";
import Amount from "../common/Amount";
import NumberUtils, {getValueAsFraction} from "../../utils/NumberUtils";
import AssetSearch from "../asset/AssetSearch";

const uuidv4 = require('uuid/v4');

/**
 * QuoteItem object
 */
export default class QuoteItem {

	constructor(public readonly id: string,
		public readonly assetId: string,
		public readonly categoryId: string,
		public readonly model: string,
		public readonly name: string,
		public readonly quantity: number,
		public readonly unitPrice: Amount,
		public readonly unitPriceWithVat: Option<Amount>,
		public readonly totalPrice: Amount,
		public readonly totalPriceWithVat: Amount,
		public readonly markup: number,
		public readonly markupAmount: Amount,
		public readonly markupAmountWithVat: Amount,
		public readonly bespokeItem: boolean,
		public readonly description: Option<string>,
		public readonly selfSourced: boolean,
		public readonly invoiceLineItem: boolean) {
	}

	static fromJson(json: QuoteItemDto): QuoteItem {
		return new QuoteItem(json.id, json.assetId ? json.assetId : json.id, '', '', json.name, json.quantity,
			new Amount(json.unitPrice), none, new Amount(json.totalPrice), new Amount(json.totalPriceWithVat), json.markup,
			new Amount(json.markupAmount), new Amount(json.markupAmountWithVat), !json.assetId, option(json.description),
			json.selfSourced, json.invoiceLineItem)
	}

	static fromAssetWithQuantity(asset: AssetSearch,
		quantity: number,
		vat: number,
		vatRegistered: boolean, partsPriceStrategy: string): QuoteItem {
		const price = asset.getPrice(partsPriceStrategy);
		const unitPrice = vatRegistered ? Amount.fromPounds(price) :
			Amount.fromPounds(NumberUtils.getValueBeforeIncreasingByPercent(price, vat));
		const totalPrice = unitPrice.multiple(quantity);
		const markupAmount = totalPrice.multiple(asset.markup / 100);
		const markupAmountWithVat = markupAmount.multiple(1 + (vat / 100));
		return new QuoteItem("",
			asset.id,
			asset.categoryId,
			asset.model,
			asset.getDisplayTextWithoutPrice(),
			quantity,
			unitPrice,
			vatRegistered ? none : option(Amount.fromPounds(price)),
			totalPrice,
			totalPrice.multiple(1 + (vat / 100)),
			asset.markup,
			markupAmount,
			markupAmountWithVat,
			false,
			none,
			false,
			false);
	}

	static createBespokeItem(name: string, categoryId: string, model: string, price: Amount, description: Option<string>,
		vatRegistered: boolean, vat: number, customPartsMarkup: number, selfSourced: boolean) {
		const id = "ba-" + uuidv4();
		let unitPrice;
		let totalPriceWithVat: Amount;
		let unitPriceWithVat: Amount;
		if (vatRegistered) {
			unitPrice = price;
			totalPriceWithVat = unitPrice.multiple(1 + (vat / 100));
		}
		else {
			unitPriceWithVat = price;
			unitPrice = new Amount(unitPriceWithVat.cents / (1 + (vat / 100)));
			totalPriceWithVat = unitPriceWithVat;
		}
		const quantity = 1;
		let markupAmount = new Amount(0), markupAmountWithVat = new Amount(0);
		let markup = 0;
		if (price.cents > 0) {
			const totalPrice = unitPrice.multiple(quantity);
			markup = customPartsMarkup;
			markupAmount = totalPrice.multiple(markup / 100);
			markupAmountWithVat = markupAmount.multiple(1 + (vat / 100));
		}
		return new QuoteItem(id,
			id,
			categoryId,
			model,
			name,
			quantity,
			unitPrice,
			option(unitPriceWithVat),
			unitPrice,
			totalPriceWithVat,
			markup,
			markupAmount,
			markupAmountWithVat,
			true,
			description,
			selfSourced,
			false);
	}

	copy({
		id = this.id,
		assetId = this.assetId,
		categoryId = this.categoryId,
		model = this.model,
		name = this.name,
		quantity = this.quantity,
		unitPrice = this.unitPrice,
		unitPriceWithVat = this.unitPriceWithVat,
		totalPrice = this.totalPrice,
		totalPriceWithVat = this.totalPriceWithVat,
		bespokeItem = this.bespokeItem,
		description = this.description,
		markup = this.markup,
		markupAmount = this.markupAmount,
		markupAmountWithVat = this.markupAmountWithVat
	}) {
		return new QuoteItem(id, assetId, categoryId, model, name, quantity, unitPrice, unitPriceWithVat, totalPrice,
			totalPriceWithVat, markup, markupAmount, markupAmountWithVat, bespokeItem, description, this.selfSourced,
			this.invoiceLineItem);
	}

	/**
	 * Get total price for item
	 * @returns {Amount}
	 */
	getTotalPrice(): Amount {
		return this.unitPriceWithVat.getOrElse(this.unitPrice).multiple(this.quantity);
	}

	calculateUnitPriceWithVat(vat: number): Amount {
		return this.unitPrice.add(this.unitPrice.multiple(vat / 100));
	}

	toJsonCreateItemDto(): CreateQuoteItemDto {
		return {
			assetId: this.bespokeItem ? undefined : this.assetId,
			name: this.name,
			quantity: this.quantity,
			unitPrice: this.unitPrice.cents,
			totalPrice: this.totalPrice.cents,
			totalPriceWithVat: this.totalPriceWithVat.cents,
			description: this.description.map(d => d).orNull,
			markup: this.markup,
			markupAmount: this.markupAmount.cents,
			markupAmountWithVat: this.markupAmountWithVat.cents,
			selfSourced: this.selfSourced
		}
	}

	/**
	 * Returns copy of this quote item with changed quantity and recalculated total prices.
	 * @param {number} newQuantity
	 * @param {number} vat
	 * @returns {QuoteItem}
	 */
	withNewQuantity(newQuantity: number, vat: number) {
		const totalPrice = this.unitPrice.multiple(newQuantity);
		const totalPriceWithVat = totalPrice.multiple(1 + (vat / 100));
		const markupAmount = totalPrice.multiple(this.markup / 100);
		const markupAmountWithVat = totalPriceWithVat.multiple(this.markup / 100);
		return this.copy({quantity: newQuantity, totalPrice, totalPriceWithVat, markupAmount, markupAmountWithVat});
	}

	/**
	 * Get net price of quote item.
	 * Unit price * quantity for VAT registered
	 * Unit price with VAT * quantity for non VAT registered.
	 * @param vatRegistered
	 */
	getNetPrice(vatRegistered: boolean = true) {
		if (vatRegistered) {
			return this.unitPrice.multiple(this.quantity)
		}
		return this.unitPriceWithVat.getOrElse(new Amount(0)).multiple(this.quantity);
	}

	/**
	 * Get part markup.
	 */
	getMarkup(vatRegistered: boolean = true) {
		if (vatRegistered) {
			return this.unitPrice.multiple(this.quantity).multiple(getValueAsFraction(this.markup));
		}
		else {
			return this.unitPriceWithVat.getOrElse(new Amount(0)).multiple(this.quantity).multiple(
				getValueAsFraction(this.markup));
		}
	}

	/**
	 * Get vat price of quote item.
	 * Vat from unit price * quantity for VAT registered
	 * 0 for non VAT registered.
	 * @param vatRegistered
	 * @param vat rate
	 */
	getVatPrice(vatRegistered: boolean = true, vat: number) {
		if (vatRegistered) {
			return this.unitPrice.multiple(this.quantity).multiple(getValueAsFraction(vat))
		}
		return new Amount(0);
	}
}
